import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { CheckCircleOutline, CancelOutlined } from '@mui/icons-material'
import type { ProductConfig, ProductName, SizeTier } from './pricingData'
import { formatPrice } from './pricingData'

interface ProductCardProps {
  product: ProductConfig
  sizeTier: SizeTier
  highlighted?: boolean
  isCurrent?: boolean
  ctaLabel?: string
  showTrial?: boolean
  loading?: boolean
  onSelect: (product: ProductName) => void
  onContactUs: () => void
}

function ProductCard({
  product,
  sizeTier,
  highlighted,
  isCurrent,
  ctaLabel,
  showTrial = true,
  loading,
  onSelect,
  onContactUs,
}: ProductCardProps) {
  const price = product.prices[sizeTier]
  const isContactUs = price === null

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: highlighted ? '2px solid #087575' : `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
        position: 'relative',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : 'background.paper',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)',
        },
      })}
    >
      <Chip
        label={isCurrent ? 'Current Plan' : 'Most popular'}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          left: 16,
          backgroundColor: isCurrent ? '#e3f2fd' : '#e8f5f5',
          color: isCurrent ? '#1565c0' : '#087575',
          fontWeight: 600,
          fontSize: '0.7rem',
          visibility: highlighted || isCurrent ? 'visible' : 'hidden',
        }}
      />

      <CardContent sx={{ flexGrow: 1, pt: 5, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {product.displayName}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, minHeight: 20 }}>
          {product.description}
        </Typography>

        {isContactUs ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Custom
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Contact us for pricing
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatPrice(price)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                per year
              </Typography>
            </Box>
            {showTrial && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {product.trialDays}-day free trial
              </Typography>
            )}
          </Box>
        )}

        <Chip
          label={`${product.adminLimit} Admin${product.adminLimit === '2' ? 's' : 's'}`}
          size="small"
          variant="outlined"
          sx={{ mb: 2, fontSize: '0.75rem' }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          This includes:
        </Typography>
        <List dense disablePadding>
          {product.features.map((feature) => (
            <ListItem key={feature.label} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {feature.included ? (
                  <CheckCircleOutline sx={{ fontSize: 18, color: '#087575' }} />
                ) : (
                  <CancelOutlined sx={{ fontSize: 18, color: '#bdbdbd' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={feature.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { color: feature.included ? 'text.primary' : 'text.disabled' },
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>

      <CardActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        {isContactUs ? (
          <Button
            fullWidth
            variant="outlined"
            onClick={onContactUs}
            sx={{
              borderRadius: 6,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#087575',
              color: '#087575',
              py: 1.2,
              '&:hover': {
                borderColor: '#065e5e',
                backgroundColor: 'rgba(8, 117, 117, 0.04)',
              },
            }}
          >
            Contact us
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            disabled={loading || isCurrent}
            onClick={() => onSelect(product.name)}
            sx={{
              borderRadius: 6,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: isCurrent ? '#9e9e9e' : '#087575',
              py: 1.2,
              '&:hover': {
                backgroundColor: isCurrent ? '#9e9e9e' : '#065e5e',
              },
            }}
          >
            {loading ? 'Loading...' : ctaLabel || 'Start free trial'}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

export default ProductCard
